// src/app/pages/procedimientos/audioTrigger/serial-trigger.service.ts

// ========== DEFINICIONES DE TIPOS PARA WEB SERIAL API ==========
// TypeScript no reconoce SerialPort por defecto (API experimental)
declare global {
  interface Navigator {
    serial: Serial;
  }

  interface Serial extends EventTarget {
    requestPort(options?: SerialPortRequestOptions): Promise<SerialPort>;
    getPorts(): Promise<SerialPort[]>;
  }

  interface SerialPortRequestOptions {
    filters?: SerialPortFilter[];
  }

  interface SerialPortFilter {
    usbVendorId?: number;
    usbProductId?: number;
  }

  interface SerialPort extends EventTarget {
    readable: ReadableStream<Uint8Array> | null;
    writable: WritableStream<Uint8Array> | null;
    open(options: SerialOptions): Promise<void>;
    close(): Promise<void>;
    getInfo(): SerialPortInfo;
  }

  interface SerialOptions {
    baudRate: number;
    dataBits?: number;
    stopBits?: number;
    parity?: 'none' | 'even' | 'odd';
    bufferSize?: number;
    flowControl?: 'none' | 'hardware';
  }

  interface SerialPortInfo {
    usbVendorId?: number;
    usbProductId?: number;
  }
}
// ================================================================

import { Injectable, EventEmitter } from '@angular/core';

/**
 * Configuración del Serial Trigger
 */
export interface SerialTriggerConfig {
  baudRate?: number; // Velocidad de comunicación (por defecto: 9600)
  tiempoEspera?: number; // Milisegundos entre disparos (por defecto: 400ms)
}

/**
 * Interface del servicio de Serial Trigger
 */
export interface ISerialTriggerService {
  onPulsoDetectado: EventEmitter<void>;
  iniciar(config?: SerialTriggerConfig): Promise<boolean>;
  detener(): void;
  ajustarTiempoEspera(ms: number): void;
  isActive(): boolean;
}

/**
 * Servicio de Serial Trigger para detectar pulsos digitales del equipo médico
 * Compatible con FT232R (0403:6001) y otros convertidores USB-Serial
 */
@Injectable({
  providedIn: 'root',
})
export class SerialTriggerService implements ISerialTriggerService {
  // EventEmitter para notificar cuando se detecta un pulso
  public onPulsoDetectado = new EventEmitter<void>();

  // Propiedades de Web Serial API
  private port: SerialPort | null = null;
  private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  private detectando = false;

  // Configuración
  private baudRate = 9600;
  private tiempoEsperaMs = 400;

  // Control de cooldown
  private ultimaDeteccion = 0;
  private cooldownActivo = false;

  // Keep-alive
  private keepAliveInterval: any = null;

  constructor() {
    console.log('🔧 SerialTriggerService inicializado');
  }

  /**
   * Inicia la detección de pulsos digitales
   */
  async iniciar(config?: SerialTriggerConfig): Promise<boolean> {
    try {
      // Verificar soporte de Web Serial API
      if (!('serial' in navigator)) {
        console.error('❌ Web Serial API no soportada en este navegador');
        console.log('💡 Usa Chrome/Edge en desktop');
        return false;
      }

      // Aplicar configuración
      if (config) {
        if (config.baudRate !== undefined) this.baudRate = config.baudRate;
        if (config.tiempoEspera !== undefined) this.tiempoEsperaMs = config.tiempoEspera;
      }

      console.log('⚡ Iniciando Serial Trigger (Modo Digital)...');
      console.log(`   Baud Rate: ${this.baudRate}`);
      console.log(`   Cooldown: ${this.tiempoEsperaMs}ms`);

      // Solicitar puerto serial
      // Filtrar por FT232R (0403:6001)
      this.port = await (navigator.serial as any).requestPort({
        filters: [
          { usbVendorId: 0x0403, usbProductId: 0x6001 }, // FT232R
        ],
      });

      if (!this.port) {
        console.error('❌ No se seleccionó ningún puerto');
        return false;
      }

      console.log('✅ Puerto serial seleccionado');

      // Abrir puerto
      await this.port.open({
        baudRate: this.baudRate,
        dataBits: 8,
        stopBits: 1,
        parity: 'none',
        flowControl: 'none',
      });

      console.log('✅ Puerto serial abierto');

      // Iniciar lectura
      this.detectando = true;
      this.leerPulsos();

      // Iniciar keep-alive
      this.iniciarKeepAlive();

      console.log('✅ Serial Trigger activado exitosamente');
      console.log('💡 Esperando pulsos del EVIS X1...');

      return true;
    } catch (error) {
      console.error('❌ Error iniciando Serial Trigger:', error);

      // Mensajes de error específicos
      if (error instanceof Error) {
        if (error.name === 'NotFoundError') {
          console.log('💡 No se encontró ningún dispositivo serial');
        } else if (error.name === 'SecurityError') {
          console.log('💡 El usuario canceló la selección');
        }
      }

      return false;
    }
  }

  /**
   * Lee pulsos digitales del puerto serial
   */
  private async leerPulsos(): Promise<void> {
    try {
      if (!this.port || !this.port.readable) {
        console.error('❌ Puerto no disponible para lectura');
        return;
      }

      this.reader = this.port.readable.getReader();
      console.log('📖 Iniciando lectura de pulsos...');
      console.log('🔍 MODO DEBUG: Mostrando TODOS los bytes recibidos');

      // 🔥 VARIABLES DE DETECCIÓN
      let estadoAnterior = 0; // 0=LOW, 1=HIGH
      let contadorLecturas = 0;
      let ultimoLog = Date.now();

      while (this.detectando && this.reader) {
        try {
          const { value, done } = await this.reader.read();

          if (done) {
            console.log('✅ Lectura terminada');
            break;
          }

          // ============ 🔥 DEBUG: Mostrar TODO lo que llega ============
          if (value && value.length > 0) {
            contadorLecturas++;

            // Mostrar CADA byte recibido
            console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
            console.log(`📡 DATOS RECIBIDOS (#${contadorLecturas})`);
            console.log(`   → Bytes totales: ${value.length}`);

            // Mostrar cada byte en diferentes formatos
            for (let i = 0; i < value.length; i++) {
              const byte = value[i];
              console.log(`   → Byte ${i}: ${byte} (decimal)`);
              console.log(`      - Hex: 0x${byte.toString(16).padStart(2, '0')}`);
              console.log(`      - Binario: ${byte.toString(2).padStart(8, '0')}`);
              console.log(`      - Char: '${String.fromCharCode(byte)}'`);
              console.log(`      - Mayor que 128? ${byte > 128 ? 'SÍ (HIGH)' : 'NO (LOW)'}`);
            }

            console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

            // 🔥 DETECCIÓN DE PULSO (código original)
            const byte = value[0]; // Primer byte

            // Considerar HIGH si byte > 128 (mitad del rango 0-255)
            const estadoActual = byte > 128 ? 1 : 0;

            console.log(
              `   🎯 Estado: ${estadoAnterior === 0 ? 'LOW' : 'HIGH'} → ${estadoActual === 0 ? 'LOW' : 'HIGH'}`,
            );

            // Detectar flanco ascendente (LOW → HIGH)
            if (estadoAnterior === 0 && estadoActual === 1) {
              const ahora = Date.now();

              // Verificar cooldown
              if (this.cooldownActivo) {
                const falta = this.tiempoEsperaMs - (ahora - this.ultimaDeteccion);
                console.log(`⏳ Pulso en cooldown (${falta}ms restantes)`);
              } else {
                console.log(`⚡⚡⚡ ¡PULSO DETECTADO! ⚡⚡⚡`);
                console.log(`   Byte: ${byte}, Lecturas: ${contadorLecturas}`);

                // Activar cooldown
                this.cooldownActivo = true;
                this.ultimaDeteccion = ahora;

                // Emitir evento
                this.onPulsoDetectado.emit();

                // Desactivar cooldown después del tiempo configurado
                setTimeout(() => {
                  this.cooldownActivo = false;
                  console.log('✅ Cooldown terminado - Listo para nuevo pulso');
                }, this.tiempoEsperaMs);
              }
            }

            estadoAnterior = estadoActual;
          } else {
            // Log cada 5 segundos si NO llegan datos
            const ahora = Date.now();
            if (ahora - ultimoLog > 5000) {
              console.log('⏸️ Sin datos en los últimos 5 segundos...');
              ultimoLog = ahora;
            }
          }
        } catch (readError) {
          if (this.detectando) {
            console.error('❌ Error leyendo datos:', readError);
          }
          break;
        }
      }
    } catch (error) {
      console.error('❌ Error en bucle de lectura:', error);
    } finally {
      if (this.reader) {
        try {
          this.reader.releaseLock();
        } catch (e) {
          // Ignorar errores al liberar
        }
        this.reader = null;
      }
      console.log('🛑 Lectura de pulsos detenida');
    }
  }

  /**
   * Detiene la detección de pulsos
   */
  async detener(): Promise<void> {
    console.log('🛑 Deteniendo Serial Trigger...');

    this.detectando = false;

    // Detener keep-alive
    if (this.keepAliveInterval) {
      clearInterval(this.keepAliveInterval);
      this.keepAliveInterval = null;
    }

    // Cancelar lectura
    if (this.reader) {
      try {
        await this.reader.cancel();
        this.reader.releaseLock();
      } catch (error) {
        console.warn('⚠️ Error cancelando reader:', error);
      }
      this.reader = null;
    }

    // Cerrar puerto
    if (this.port) {
      try {
        await this.port.close();
      } catch (error) {
        console.warn('⚠️ Error cerrando puerto:', error);
      }
      this.port = null;
    }

    this.cooldownActivo = false;
    this.ultimaDeteccion = 0;

    console.log('✅ Serial Trigger detenido');
  }

  /**
   * Keep-alive para mantener conexión activa
   */
  private iniciarKeepAlive(): void {
    this.keepAliveInterval = setInterval(() => {
      if (this.port && this.detectando) {
        console.log('💚 Serial Trigger activo (heartbeat)');
      }
    }, 60000); // Cada 60 segundos
  }

  /**
   * Ajusta el tiempo de espera entre disparos
   */
  ajustarTiempoEspera(ms: number): void {
    if (ms < 100) {
      console.warn('⚠️ Tiempo de espera muy corto, mínimo 100ms');
      this.tiempoEsperaMs = 100;
    } else if (ms > 5000) {
      console.warn('⚠️ Tiempo de espera muy largo, máximo 5000ms');
      this.tiempoEsperaMs = 5000;
    } else {
      this.tiempoEsperaMs = ms;
      console.log(`✅ Tiempo de espera ajustado: ${ms}ms`);
    }
  }

  /**
   * Verifica si el servicio está activo
   */
  isActive(): boolean {
    return this.detectando;
  }
}
